<template>
  <span v-if="visible" class="sf">
    <ElSelect
      ref="elSelect"
      v-model="input"
      class="sf-select"
      popper-class="sf-popper"
      :clearable="clearable"
      :filterable="hasData"
      :filter-method="filter"
      :placeholder="label"
      @visible-change="dropdownChange"
      @change="autoSearchHandle"
    >
      <EditableOptions
        :options-data="optionsData || []"
        :editor="editor"
        :reload="reload"
      >
        <slot />
      </EditableOptions>
    </ElSelect>
  </span>
</template>

<script lang="ts">
/**
 * ### 单选下拉组件
 * <warning> 获取下拉数据可从url/transform/callback三种方式中三选一
 * <warning> 可通过默认插槽渲染下拉项
 */
import {
  ref,
  PropType,
  nextTick,
  defineComponent,
  ComponentPublicInstance
} from 'vue';
import { ElSelect } from 'element-plus';
import { buildSearchFieldProps, setupSearchField } from '../../SearchField';
import {
  pinyinFilterData,
  PinyinFilterCallback
} from '../../../../core/CommonInternal';
import { isTransformRawId } from '../../../../core/Transform';
import { DropdownEditor } from '../../../../common/DropdownEditor';
import EditableOptions from '../../../../common/EditableOptions.vue';

const ValueCtor = Number;

const props = buildSearchFieldProps(ValueCtor, {
  /**
   * ### 获取下拉数据的url
   * <warning> 最低优先级
   */
  url: { type: String },
  /**
   * ### 获取下拉数据的数据源（变换）
   * <warning> 优先级最高
   */
  transform: { type: String },
  /**
   * ### 获取下拉数据的函数
   * <warning> 优先级高于url低于transform
   */
  callback: {
    type: Function as PropType<PinyinFilterCallback>
  },
  /**
   * ### 如若使用callback方式获取下拉数据 改选项会从查询条件中获取同名key作为callback参数
   */
  callbackParams: { type: Array as PropType<string[]> },
  /**
   * ### 排除下拉数据中的特定ID
   */
  excludes: { type: Array as PropType<number[]> },
  /**
   * ### 选择变更时,是否发起列表查询
   */
  autoSearch: { type: Boolean },
  /**
   * ### 选中内容是否可清除
   */
  clearable: { type: Boolean, default: true },
  /**
   * ### 下拉列表编辑器
   */
  editor: { type: Object as PropType<DropdownEditor> }
} as const);

export default defineComponent({
  components: { EditableOptions },
  extends: ElSelect as any,
  props,

  setup(props, ctx) {
    return setupSearchField(
      ValueCtor,
      props,
      ctx,
      ({ submit }) => {
        const { optionsData, filter, clearFilter, reload, hasData } =
          pinyinFilterData(
            props.url,
            props.transform,
            props.callback,
            props.callbackParams,
            false,
            props.excludes,
            props.rawId
          );

        let elSelect = ref<ComponentPublicInstance<{
          focus: () => void;
          blur: () => void;
          input: HTMLInputElement;
        }> | null>(null);

        // 自动查询处理
        const autoSearchHandle = () => {
          // 清除fliter
          clearFilter(false);

          const input = elSelect.value!.input;
          if (input) {
            nextTick(() => {
              // 清空输入框
              input.value = '';
              input.dispatchEvent(new Event('input'));
            });
          }

          if (props.autoSearch) {
            submit();
          }
        };

        const dropdownChange = (show: boolean) => {
          clearFilter(show);

          if (!show) {
            nextTick(() => elSelect.value?.blur());
          }
        };

        return {
          optionsData,
          filter,
          clearFilter,
          hasData,
          reload,
          autoSearchHandle,
          elSelect,
          dropdownChange
        };
      },
      // 扩展参数
      {
        defaultTranslate: (v: number | undefined) => {
          return v;
        },

        // 优先使用transform的rawId
        rawId: props.transform ? isTransformRawId(props.transform) : props.rawId
      }
    );
  }
});
</script>

<style lang="scss" scoped>
.sf-select {
  width: 100%;
  height: var(--sf-line-height);
  font-size: var(--sf-font-size);

  --el-font-size-base: var(--sf-font-size);

  :deep(.el-input) {
    --el-input-height: var(--sf-line-height);
    --el-input-icon-color: #8f959e;
  }

  :deep(.select-trigger) {
    height: var(--sf-line-height);
  }

  :deep(.el-input__wrapper) {
    height: calc(var(--sf-line-height) - 2px) !important;
    box-shadow: none;
    border-radius: var(--sf-border-radius);
    border: 1px solid var(--sf-border-color);
    padding: 0 2px;
  }

  :deep(.el-input__wrapper:hover) {
    box-shadow: none !important;
    border-color: var(--sf-border-hover-color);
  }

  :deep(.el-input.is-focus) {
    box-shadow: none;
  }

  :deep(.el-input.is-focus .el-input__wrapper) {
    box-shadow: none !important;
    border-color: var(--sf-border-hover-color);
  }

  :deep(.el-input__prefix) {
    max-width: var(--sf-prefix-width);
    margin-right: var(--sf-prefix-right-space);
  }

  :deep(.el-input__prefix-inner) {
    display: block;
    max-width: var(--sf-prefix-width);
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
  }

  :deep(.el-input__prefix-inner:hover) {
    overflow: visible;
  }

  :deep(.el-input__inner) {
    padding-left: 8px;
  }

  :deep(.el-input__suffix) {
    margin-right: 8px;
  }
}
</style>

<style>
/* dropdown部分被传送到了body,使不了scoped */

/* .sf-popper .el-select-dropdown__item
{
	font-size: 14px;
} */
</style>
